import {FC, useState} from 'react'
import {api, Dispute} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

const STATUS_META: Record<string, {label: string; bg: string; color: string}> = {
  open:         {label: 'Open',         bg: '#fee2e2', color: '#dc2626'},
  under_review: {label: 'Under review', bg: '#fef9c3', color: '#ca8a04'},
  resolved:     {label: 'Resolved',     bg: '#dcfce7', color: '#16a34a'},
  rejected:     {label: 'Rejected',     bg: '#f3f4f6', color: '#6b7280'},
}

const STATUS_OPTIONS = ['open', 'under_review', 'resolved', 'rejected']

const fmtDate = (iso: string): string => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

const DisputesPanel: FC<{showHeader?: boolean}> = ({showHeader = true}) => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [saving, setSaving]     = useState(false)

  const {items: disputes, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated(
      (p, l, s) => api.getDisputesPaged(p, l, s, {status: statusFilter !== 'all' ? statusFilter : undefined}),
      {limit: 20, deps: [statusFilter]},
    )

  // Manage modal
  const [target, setTarget]     = useState<Dispute | null>(null)
  const [fStatus, setFStatus]   = useState('open')
  const [fNote, setFNote]       = useState('')
  const [fRefund, setFRefund]   = useState(false)
  const [fRefundAmount, setFRefundAmount] = useState('')
  const [formError, setFormError] = useState('')

  const openManage = (d: Dispute) => {
    setTarget(d)
    setFStatus(d.status)
    setFNote(d.adminNote ?? '')
    setFRefund(false)
    setFRefundAmount(String(d.refundedAmount || d.amount || ''))
    setFormError('')
  }

  const handleCancelReverse = async () => {
    if (!target) return
    const label = target.linked?.kind === 'booking' ? 'booking' : 'ticket'
    if (!window.confirm(`Cancel this ${label} and reverse ALL of the buyer's money to their wallet? This also claws back the organizer/provider earnings. This can't be undone.`)) return
    setSaving(true); setFormError('')
    try {
      await api.cancelReverseDispute(target.id, fNote || undefined)
      reload()
      setTarget(null)
      toast('Cancelled & reversed — buyer refunded')
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to cancel & reverse.')
      toast(e?.message ?? 'Cancel & reverse failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!target) return
    setSaving(true); setFormError('')
    try {
      let message: string
      if (fRefund) {
        if (!target.user) throw new Error('This dispute has no linked buyer to refund.')
        await api.resolveDispute(target.id, {
          refund: true,
          refundAmount: fRefundAmount ? parseFloat(fRefundAmount) : undefined,
          note: fNote || undefined,
        })
        message = 'Dispute resolved & refunded'
      } else {
        await api.updateDispute(target.id, {status: fStatus, adminNote: fNote})
        message = 'Dispute updated'
      }
      reload()
      setTarget(null)
      toast(message)
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
      toast(e?.message ?? 'Save failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }
  const labelStyle = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}

  const StatusBadge: FC<{status: string}> = ({status}) => {
    const m = STATUS_META[status] ?? STATUS_META.open
    return <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: m.bg, color: m.color}}>{m.label}</span>
  }

  return (
    <div>
      {/* Header */}
      {showHeader && (
        <div style={{marginBottom: 24}}>
          <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Disputes</h1>
          <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Review buyer-filed disputes and Stripe chargebacks; resolve and refund</p>
        </div>
      )}

      {/* Toolbar */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', gap: 12, alignItems: 'center'}}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search by ref, reason or buyer...' value={search}
            onChange={e => setSearch(e.target.value)} style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{...inputStyle, width: 170, background: '#fff'}}>
          <option value='all'>All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['REF', 'BUYER', 'REASON', 'AMOUNT', 'SOURCE', 'STATUS', 'FILED', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {disputes.map((d, i) => (
                  <tr key={d.id} style={{borderBottom: i < disputes.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                    <td style={{padding: '14px 20px', fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>{d.disputeRef}</td>
                    <td style={{padding: '14px 20px', minWidth: 160}}>
                      {d.user
                        ? <div><div style={{fontWeight: 600, color: '#0f172a'}}>{d.user.firstName} {d.user.lastName}</div><div style={{fontSize: 12, color: '#94a3b8'}}>{d.user.email}</div></div>
                        : <span style={{color: '#94a3b8'}}>—</span>}
                    </td>
                    <td style={{padding: '14px 20px', minWidth: 160, color: '#374151'}}>{d.reason}</td>
                    <td style={{padding: '14px 20px', fontWeight: 600, color: '#0f172a'}}>${Number(d.amount).toFixed(2)}</td>
                    <td style={{padding: '14px 20px'}}>
                      <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: d.source === 'stripe' ? '#ede9fe' : '#e0f2fe', color: d.source === 'stripe' ? '#7c3aed' : '#0369a1'}}>
                        {d.source === 'stripe' ? 'Chargeback' : 'In-app'}
                      </span>
                    </td>
                    <td style={{padding: '14px 20px'}}><StatusBadge status={d.status} /></td>
                    <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDate(d.createdAt)}</td>
                    <td style={{padding: '14px 20px'}}>
                      <button onClick={() => openManage(d)}
                        style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
                {disputes.length === 0 && (
                  <tr><td colSpan={8} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No disputes found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {disputes.length} of {total} disputes</span>
            <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: '#fee2e2', color: '#dc2626'}}>
              Open: {disputes.filter(d => d.status === 'open' || d.status === 'under_review').length}
            </span>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      {/* Manage Modal */}
      {target && (
        <div onClick={() => setTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.15)'}}>
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>Dispute {target.disputeRef}</h3>
              <button onClick={() => setTarget(null)} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 16}}>
              {formError && <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>{formError}</div>}

              {/* Read-only summary */}
              <div style={{background: '#f8fafc', borderRadius: 10, padding: 16, fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 8}}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Buyer</span><span>{target.user ? `${target.user.firstName} ${target.user.lastName}` : '—'}</span></div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Amount</span><span style={{fontWeight: 600}}>${Number(target.amount).toFixed(2)}</span></div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Source</span><span>{target.source === 'stripe' ? 'Stripe chargeback' : 'In-app'}</span></div>
                {target.transactionRef && <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Transaction</span><span style={{fontFamily: 'monospace', fontSize: 12}}>{target.transactionRef}</span></div>}
                {/* Linked entity — what this dispute is actually about */}
                {target.linked && target.linked.kind !== 'unknown' && (
                  <>
                    <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Type</span><span style={{fontWeight: 600}}>{target.linked.kindLabel}</span></div>
                    {(target.linked.eventName || target.linked.providerName) && (
                      <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>{target.linked.kind === 'booking' ? 'Provider' : 'Event'}</span><span>{target.linked.eventName || target.linked.providerName}</span></div>
                    )}
                    {target.linked.eventDate && <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>Date</span><span>{String(target.linked.eventDate).split(' ')[0]}</span></div>}
                    {target.linked.entityStatus && <div style={{display: 'flex', justifyContent: 'space-between'}}><span style={{color: '#94a3b8'}}>{target.linked.kindLabel} status</span><span style={{textTransform: 'capitalize'}}>{target.linked.entityStatus}</span></div>}
                  </>
                )}
                <div><span style={{color: '#94a3b8'}}>Reason: </span>{target.reason}</div>
                {target.details && <div><span style={{color: '#94a3b8'}}>Details: </span>{target.details}</div>}
                {target.refundedAmount > 0 && <div style={{color: '#16a34a', fontWeight: 600}}>Refunded ${Number(target.refundedAmount).toFixed(2)} to wallet</div>}
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inputStyle} disabled={fRefund}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Admin note</label>
                <textarea value={fNote} onChange={e => setFNote(e.target.value)} rows={3}
                  placeholder='Internal note / resolution summary' style={{...inputStyle, resize: 'vertical' as const}} />
              </div>

              {/* Refund */}
              <div style={{border: '1px solid #e2e8f0', borderRadius: 10, padding: 14}}>
                <label style={{display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer'}}>
                  <input type='checkbox' checked={fRefund} onChange={e => setFRefund(e.target.checked)} disabled={!target.user} />
                  <span style={{fontSize: 14, fontWeight: 600, color: '#0f172a'}}>Resolve & refund to wallet</span>
                </label>
                {fRefund && (
                  <div style={{marginTop: 12}}>
                    <label style={labelStyle}>Refund amount ($)</label>
                    <input type='number' value={fRefundAmount} onChange={e => setFRefundAmount(e.target.value)} style={inputStyle} />
                  </div>
                )}
                {target.source === 'stripe' && (
                  <div style={{marginTop: 10, fontSize: 12, color: '#ca8a04'}}>
                    This is a Stripe chargeback — the card network already returns the funds. Only issue a wallet refund if you also owe the buyer separately.
                  </div>
                )}
                {!target.user && <div style={{marginTop: 10, fontSize: 12, color: '#94a3b8'}}>No linked buyer — wallet refund unavailable.</div>}
              </div>

              {/* Cancel the ticket/booking & reverse everything */}
              {target.linked && target.linked.kind !== 'unknown' && (
                <div style={{border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: 14}}>
                  <div style={{fontSize: 14, fontWeight: 700, color: '#b91c1c', marginBottom: 4}}>Cancel {target.linked.kindLabel} & reverse all money</div>
                  <div style={{fontSize: 12, color: '#7f1d1d', marginBottom: 12}}>
                    Cancels the {target.linked.kind === 'booking' ? 'booking' : 'ticket'}, refunds the buyer's full payment to their wallet, and claws back the {target.linked.kind === 'booking' ? 'provider' : 'organizer'} earnings. Marks the dispute resolved.
                  </div>
                  {target.linked.cancelable ? (
                    <button onClick={handleCancelReverse} disabled={saving}
                      style={{padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                      {saving ? 'Working…' : `Cancel & reverse ($${Number(target.linked.entityAmount).toFixed(2)})`}
                    </button>
                  ) : (
                    <div style={{fontSize: 12, color: '#94a3b8'}}>Already cancelled/refunded — nothing to reverse.</div>
                  )}
                </div>
              )}
            </div>

            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff'}}>
              <button onClick={() => setTarget(null)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : fRefund ? 'Resolve & Refund' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const DisputesPageWrapper: FC = () => <DisputesPanel />

export {DisputesPageWrapper, DisputesPanel}
